import type { BoardCase, BoardTask } from "@/api/board/boardCaseFields";

/**
 * Sichtbare Meldungen unter den Pflegefällen.
 * Eine care_due-Meldung entfällt, wenn ihr Schlüssel exakt eine offene
 * Aufgabe desselben Falls trifft (Fall, Art, occurrence_key oder die ersten
 * 16 Zeichen von due_at — so baut der Sweep den Key).
 * Übrige Meldungen desselben Falls werden zu einer Zeile zusammengezogen.
 */
export type BoardNotice = {
  id: string;
  title: string;
  category: string;
  body: string | null;
  read_at: string | null;
  created_at: string;
  entity_id: string | null;
  dedupe_key?: string | null;
};

export type BoardNoticeRow =
  | { kind: "single"; id: string; note: BoardNotice }
  | {
      kind: "summary";
      id: string;
      caseId: string;
      title: string;
      notes: BoardNotice[];
    };

const CARE_DUE_PREFIX = "care_due:";

function taskStamp(task: BoardTask): string | null {
  const occurrence = task.occurrence_key?.trim() ?? "";
  if (occurrence) return occurrence;
  const due = task.due_at?.trim() ?? "";
  if (due) return due.slice(0, 16);
  return null;
}

/** true, wenn die Meldung dieselbe offene Aufgabe schon in der Tabelle steht. */
export function notificationDuplicatesOpenTask(
  note: Pick<BoardNotice, "entity_id" | "dedupe_key">,
  cases: readonly Pick<BoardCase, "case_id" | "open_tasks">[],
): boolean {
  const key = note.dedupe_key?.trim() ?? "";
  const caseId = note.entity_id?.trim() ?? "";
  if (!caseId || !key.startsWith(CARE_DUE_PREFIX)) return false;
  const rest = key.slice(CARE_DUE_PREFIX.length);
  const prefix = `${caseId}:`;
  if (!rest.startsWith(prefix)) return false;
  const afterCase = rest.slice(prefix.length);
  const splitAt = afterCase.indexOf(":");
  if (splitAt <= 0) return false;
  const kind = afterCase.slice(0, splitAt);
  const stamp = afterCase.slice(splitAt + 1);
  if (!stamp) return false;
  const row = cases.find((item) => item.case_id === caseId);
  if (!row) return false;
  return (row.open_tasks ?? []).some((task) => task.kind === kind && taskStamp(task) === stamp);
}

function caseNumberFrom(
  caseId: string,
  notes: readonly BoardNotice[],
  cases: readonly Pick<BoardCase, "case_id" | "case_number">[],
): string {
  const fromBoard = cases.find((item) => item.case_id === caseId)?.case_number?.trim() ?? "";
  if (fromBoard) return fromBoard;
  const title = notes[0]?.title ?? "";
  const head = title.split(":")[0]?.trim() ?? "";
  return head || "Fall";
}

export function visibleBoardNotifications(
  notes: readonly BoardNotice[],
  cases: readonly Pick<BoardCase, "case_id" | "case_number" | "open_tasks">[],
): BoardNoticeRow[] {
  const kept = notes.filter((note) => !notificationDuplicatesOpenTask(note, cases));
  const groups = new Map<string, BoardNotice[]>();
  const order: string[] = [];
  for (const note of kept) {
    const key = note.entity_id?.trim() ? note.entity_id : `solo:${note.id}`;
    const bucket = groups.get(key);
    if (!bucket) {
      groups.set(key, [note]);
      order.push(key);
    } else {
      bucket.push(note);
    }
  }
  const rows: BoardNoticeRow[] = [];
  for (const key of order) {
    const items = groups.get(key) ?? [];
    const first = items[0];
    if (!first) continue;
    if (items.length === 1 || key.startsWith("solo:")) {
      rows.push({ kind: "single", id: first.id, note: first });
      continue;
    }
    const number = caseNumberFrom(key, items, cases);
    rows.push({
      kind: "summary",
      id: `summary:${key}`,
      caseId: key,
      title: `${number}: ${items.length} Meldungen`,
      notes: items,
    });
  }
  return rows;
}

export function shownUnreadCount(rows: readonly BoardNoticeRow[]): number {
  let count = 0;
  for (const row of rows) {
    const notes = row.kind === "single" ? [row.note] : row.notes;
    count += notes.filter((note) => !note.read_at).length;
  }
  return count;
}
